import { createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import type { SsrPayload, SsrRouteId } from '@/types/ssr';

const SsrPayloadContext = createContext<SsrPayload | null>(null);

function getComparableLocation(urlString: string) {
  try {
    const url = new URL(urlString, 'https://mapleos.ai');
    return `${url.pathname}${url.search}`;
  } catch {
    return urlString;
  }
}

export function SsrPayloadProvider({
  payload,
  children,
}: {
  payload: SsrPayload | null;
  children: React.ReactNode;
}) {
  return <SsrPayloadContext.Provider value={payload}>{children}</SsrPayloadContext.Provider>;
}

export function useSsrPayload() {
  return useContext(SsrPayloadContext);
}

export function useRouteSsrData<T>(routeIds: SsrRouteId | SsrRouteId[]) {
  const payload = useSsrPayload();
  const location = useLocation();
  const allowedRouteIds = Array.isArray(routeIds) ? routeIds : [routeIds];

  if (!payload?.routeId || !allowedRouteIds.includes(payload.routeId)) {
    return null;
  }

  const payloadLocation = getComparableLocation(payload.url);
  const currentLocation = `${location.pathname}${location.search}`;

  if (payloadLocation !== currentLocation) {
    return null;
  }

  return (payload.data ?? null) as T | null;
}
